import React from 'react';
import ScrollableTable from '../ScrollableTable';
import TablePagination from '../TablePagination';
import EmptyState from './EmptyState';
import LoadingRows from './LoadingRows';
import { ds } from '../../design-system';

/**
 * Central list-page data table.
 * Owns sticky header/first column (via ScrollableTable), header/row styling
 * (via data-list-table + design tokens), empty/loading, and pagination
 * via the shared TablePagination component (do not add custom page controls).
 *
 * Pass page, totalPages, pageSize, count, onPageChange, onPageSizeChange
 * (or spread useListPagination().paginationProps).
 */
export default function DataTable({
  columns = [],
  children,
  loading = false,
  empty = false,
  emptyTitle = 'No records found',
  emptyHint,
  emptyIcon,
  colSpan,
  minWidth = '72rem',
  stickyFirstColumn = true,
  mobileScrollHint,
  ariaLabel = 'Data table',
  // pagination
  page,
  totalPages,
  pageSize,
  count,
  onPageChange,
  onPageSizeChange,
  paginationDisabled = false,
  rowsPerPageId = 'data-table-rows',
  className = '',
  tableClassName = '',
}) {
  const span = colSpan || columns.length || 1;

  return (
    <div className={`${ds.card} ${className}`}>
      <ScrollableTable
        stickyFirstColumn={stickyFirstColumn}
        mobileScrollHint={mobileScrollHint}
        ariaLabel={ariaLabel}
      >
        <table
          className={`${ds.tableShell} ${tableClassName}`.trim()}
          style={{ minWidth }}
          data-list-table
        >
          {columns.length ? (
            <thead>
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.key || col.label}
                    className={`${ds.th} ${col.headerClassName || ''}`.trim()}
                    style={col.width ? { width: col.width } : undefined}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
          ) : null}
          <tbody>
            {loading ? (
              <LoadingRows rows={5} cols={span} />
            ) : empty ? (
              <tr>
                <td colSpan={span}>
                  <EmptyState title={emptyTitle} hint={emptyHint} icon={emptyIcon} />
                </td>
              </tr>
            ) : (
              children
            )}
          </tbody>
        </table>
      </ScrollableTable>

      {onPageChange ? (
        <TablePagination
          page={page}
          totalPages={totalPages || 1}
          onPageChange={onPageChange}
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
          disabled={paginationDisabled || loading}
          count={count || 0}
          rowsPerPageId={rowsPerPageId}
          embedded
          className="px-4"
        />
      ) : null}
    </div>
  );
}

export { ds };
